import {
    MATRIX_RTC_CALL_NOTIFY,
    MATRIX_RTC_NOTIFICATION
} from "src/constants/matrixEventTypes";
import {
    MEETING_EVENT_METADATA_FIELD,
    MeetingEventMetadata,
    SHARED_CALL_SUMMARY_FIELD,
    SharedCallSummaryUpdate
} from "./meetingCallEventContract";

type MeetingNotificationMode = "notification" | "ring";

type MeetingNotificationClient = {
    sendEvent: (
        roomId: string,
        eventType: string,
        content: Record<string, unknown>
    ) => Promise<{ event_id?: string }>;
};

type SendMeetingNotificationsParameters = {
    additionalContent?: Record<string, unknown>;
    callId: string;
    lifetime: number;
    meetingMetadata?: MeetingEventMetadata;
    mode: MeetingNotificationMode;
    recipientIds: string[];
    roomId: string;
    senderTimestamp: number;
    summary?: SharedCallSummaryUpdate;
};

export type MeetingNotificationResult = {
    callNotifyEventId: string;
    rtcNotificationEventId: string;
};

export const sendMeetingNotifications = async (
    matrixClient: MeetingNotificationClient,
    parameters: SendMeetingNotificationsParameters
): Promise<MeetingNotificationResult> => {
    const callNotifyContent: Record<string, unknown> = {
        ...parameters.additionalContent,
        application: "m.call",
        "m.mentions": { user_ids: parameters.recipientIds },
        notify_type: parameters.mode,
        call_id: parameters.callId,
        sender_ts: parameters.senderTimestamp,
        lifetime: parameters.lifetime,
        ...(parameters.meetingMetadata ? {
            [MEETING_EVENT_METADATA_FIELD]: parameters.meetingMetadata
        } : {}),
        ...(parameters.summary ? { [SHARED_CALL_SUMMARY_FIELD]: parameters.summary } : {})
    };
    const rtcNotificationContent: Record<string, unknown> = {
        ...callNotifyContent,
        notification_type: parameters.mode
    };
    const [callNotifyResult, rtcNotificationResult] = await Promise.allSettled([
        matrixClient.sendEvent(parameters.roomId, MATRIX_RTC_CALL_NOTIFY, callNotifyContent),
        matrixClient.sendEvent(parameters.roomId, MATRIX_RTC_NOTIFICATION, rtcNotificationContent)
    ]);

    if (callNotifyResult.status === "rejected" && rtcNotificationResult.status === "rejected") {
        throw callNotifyResult.reason;
    }
    if (callNotifyResult.status === "rejected") {
        console.warn("Unable to send the Matrix call notification event.", callNotifyResult.reason);
    }
    if (rtcNotificationResult.status === "rejected") {
        console.warn("Unable to send the MatrixRTC notification event.", rtcNotificationResult.reason);
    }

    return {
        callNotifyEventId: callNotifyResult.status === "fulfilled"
            ? callNotifyResult.value.event_id || ""
            : "",
        rtcNotificationEventId: rtcNotificationResult.status === "fulfilled"
            ? rtcNotificationResult.value.event_id || ""
            : ""
    };
};
